import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useUser } from "../hooks/useUser";

/* --- SVG Goo filter --- */
const GooFilter = () => (
  <svg
    style={{ position: "absolute", width: 0, height: 0, overflow: "hidden" }}
  >
    <defs>
      <filter id="goo-effect" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur in="SourceGraphic" result="blur" stdDeviation="7.2" />
        <feColorMatrix
          in="blur"
          mode="matrix"
          values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 22 -8.5"
          result="goo"
        />
        <feBlend in2="goo" in="SourceGraphic" result="mix" />
      </filter>
    </defs>
  </svg>
);

/* --- Canvas particles --- */
function ParticleCanvas({
  colors,
  count,
  speed,
  sizeMax,
  sizeMin,
  opacity,
  blur,
  active,
}) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const psRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const rand = (a, b) => Math.random() * (b - a) + a;
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const w = canvas.width,
      h = canvas.height;
    psRef.current = {
      ctx,
      w,
      h,
      ps: Array.from({ length: count }, () => ({
        x: rand(0, w),
        y: rand(0, h),
        r: rand(sizeMin, sizeMax),
        vx: rand(-speed, speed),
        vy: rand(-speed, speed),
        color: pick(colors),
        alpha: rand(0.3, opacity),
      })),
    };
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  useEffect(() => {
    if (!active || !psRef.current) {
      cancelAnimationFrame(animRef.current);
      return;
    }
    const { ctx, w, h, ps } = psRef.current;
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      ps.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -p.r) p.x = w + p.r;
        if (p.x > w + p.r) p.x = -p.r;
        if (p.y < -p.r) p.y = h + p.r;
        if (p.y > h + p.r) p.y = -p.r;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
      });
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [active]);
  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        filter: blur ? "blur(" + blur + "px)" : undefined,
      }}
    />
  );
}

/* --- Icons --- */
const TorrentIcon = () => (
  <svg viewBox="0 0 20 20" style={{ width: 20, height: 20 }}>
    <defs>
      <linearGradient id="ig1" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#9699a8" />
      </linearGradient>
    </defs>
    <path
      fill="url(#ig1)"
      d="M10 .4C4.698.4.4 4.698.4 10s4.298 9.6 9.6 9.6 9.6-4.298 9.6-9.6S15.302.4 10 .4zm0 4.8l5.4 4.8H12.2v5.4H7.8v-5.4H4.6L10 5.2z"
    />
  </svg>
);
const ArchiveIcon = () => (
  <svg viewBox="0 0 20 20" style={{ width: 20, height: 20 }}>
    <defs>
      <linearGradient id="ig2" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#9699a8" />
      </linearGradient>
    </defs>
    <path
      fill="url(#ig2)"
      d="M13.981 2H6.018s-.996 0-.996 1h9.955c0-1-.996-1-.996-1zm2.987 3c0-1-.995-1-.995-1H4.027s-.995 0-.995 1v1h13.936V5zm1.99 1l-.588-.592V7H1.63V5.408L1.041 6C.452 6.592.03 6.75.267 8c.236 1.246 1.379 8.076 1.549 9 .186 1.014 1.217 1 1.217 1h13.936s1.03.014 1.217-1c.17-.924 1.312-7.754 1.549-9 .235-1.25-.187-1.408-.777-2zM14 11.997c0 .554-.449 1.003-1.003 1.003H7.003A1.003 1.003 0 0 1 6 11.997V10h1v2h6v-2h1v1.997z"
    />
  </svg>
);
const ArchiveArrowBackIcon = () => (
  <svg viewBox="0 0 20 20" style={{ width: 20, height: 20, transform: "scaleX(-1)" }}>
    <defs>
      <linearGradient id="ig3" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="100%" stopColor="#9699a8" />
      </linearGradient>
    </defs>
    <path
      fill="url(#ig3)"
      d="M2 4.25C2 3.56 2.56 3 3.25 3h13.5c.69 0 1.25.56 1.25 1.25v1.5C18 6.44 17.44 7 16.75 7H3.25C2.56 7 2 6.44 2 5.75zM3 8h14v1.6a5.5 5.5 0 0 0-2.5-.6c-1.177 0-2.268.37-3.163 1H8.5a.5.5 0 0 0 0 1h1.757A5.48 5.48 0 0 0 9 14.5c0 .9.216 1.75.6 2.5H6a3 3 0 0 1-3-3zm11.5 11a4.5 4.5 0 1 0 0-9a4.5 4.5 0 0 0 0 9m-.896-6.396l-.897.896h1.543A2.75 2.75 0 0 1 17 16.25v.25a.5.5 0 0 1-1 0v-.25a1.75 1.75 0 0 0-1.75-1.75h-1.543l.897.896a.5.5 0 0 1-.708.708l-1.752-1.753a.5.5 0 0 1 .002-.705l1.75-1.75a.5.5 0 0 1 .708.708"
    />
  </svg>
);

/* --- Transitions --- */
const tInstant = { duration: 0, type: "tween" };
const tSp1 = { type: "spring", bounce: 0.2, duration: 1 };
const tSp2 = { type: "spring", bounce: 0.2, duration: 1.5 };
const tSp3 = { type: "spring", bounce: 0.2, duration: 2.5 };

/* --- Animation values --- */
const GLOW_SHADOW = {
  default:
    "0px 0px 0px 0px rgba(255,255,255,0), 0px 0.92px 0.92px -0.41px rgba(63,63,232,0.19), 0px 2.19px 2.19px -0.81px rgba(63,63,232,0.19), 0px 3.99px 3.99px -1.22px rgba(63,63,232,0.19), 0px 6.64px 6.64px -1.63px rgba(63,63,232,0.18), 0px 10.72px 10.72px -2.03px rgba(63,63,232,0.18), 0px 17.55px 17.55px -2.44px rgba(63,63,232,0.17), 0px 30.22px 30.22px -2.84px rgba(63,63,232,0.15), 0px 55px 55px -3.25px rgba(63,63,232,0.105), 0px 0px 0px 0px rgba(82,130,235,0.3)",
  hoverStart:
    "0px 11px 14px -10px rgb(255,255,255), 0px 0.92px 0.92px -0.41px rgba(63,63,232,0.39), 0px 2.19px 2.19px -0.81px rgba(63,63,232,0.38), 0px 3.99px 3.99px -1.22px rgba(63,63,232,0.38), 0px 6.64px 6.64px -1.63px rgba(63,63,232,0.37), 0px 10.72px 10.72px -2.03px rgba(63,63,232,0.35), 0px 17.55px 17.55px -2.44px rgba(63,63,232,0.33), 0px 30.22px 30.22px -2.84px rgba(63,63,232,0.29), 0px 55px 55px -3.25px rgba(63,63,232,0.21), 0px 40px 60px -10px rgba(82,130,235,0.15)",
  hoverEnd:
    "0px 4px 15px -8px rgb(255,255,255), 0px 0.92px 0.92px -0.41px rgba(63,63,232,0.19), 0px 2.19px 2.19px -0.81px rgba(63,63,232,0.19), 0px 3.99px 3.99px -1.22px rgba(63,63,232,0.19), 0px 6.64px 6.64px -1.63px rgba(63,63,232,0.18), 0px 10.72px 10.72px -2.03px rgba(63,63,232,0.18), 0px 17.55px 17.55px -2.44px rgba(63,63,232,0.17), 0px 30.22px 30.22px -2.84px rgba(63,63,232,0.15), 0px 55px 55px -3.25px rgba(63,63,232,0.105), 0px 0px 10px -10px rgba(82,130,235,0.2)",
  mouseLeave:
    "0px -17px 24px -8px rgba(255,255,255,0), 0px 0.92px 0.92px -0.41px rgba(63,63,232,0), 0px 2.19px 2.19px -0.81px rgba(63,63,232,0), 0px 3.99px 3.99px -1.22px rgba(63,63,232,0), 0px 6.64px 6.64px -1.63px rgba(63,63,232,0), 0px 10.72px 10.72px -2.03px rgba(63,63,232,0), 0px 17.55px 17.55px -2.44px rgba(63,63,232,0), 0px 30.22px 30.22px -2.84px rgba(63,63,232,0), 0px 55px 55px -3.25px rgba(63,63,232,0), 0px -37px 50px -10px rgba(63,63,232,0)",
};
const BORDER_MASK = {
  default:
    "radial-gradient(93% 98.8% at 50% 64.3%, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 0%) add",
  hoverStart:
    "radial-gradient(93% 98.8% at 50% 64.3%, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%) add",
  hoverEnd:
    "radial-gradient(93% 98.8% at 50% 64.3%, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%) add",
  mouseLeave:
    "radial-gradient(93% 98.8% at 50% 64.3%, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%) add",
};
const BORDER_BG = {
  default:
    "linear-gradient(180deg, rgb(126,133,181) 0%, rgb(126,133,181) 100%)",
  hoverStart:
    "linear-gradient(180deg, rgb(126,133,181) 0%, rgb(201,245,255) 100%)",
  hoverEnd:
    "linear-gradient(180deg, rgb(126,133,181) 0%, rgb(201,245,255) 100%)",
  mouseLeave:
    "linear-gradient(0deg, rgb(126,133,181) 0%, rgb(126,133,181) 100%)",
};
const BLUE_GLOW_BG = {
  default:
    "radial-gradient(58% 75% at 50% 100%, rgba(19,49,112,0) 0%, rgba(21,18,84,0) 100%)",
  hoverStart:
    "radial-gradient(58% 75% at 50% 100%, rgb(19,49,112) 0%, rgba(21,18,84,0) 100%)",
  hoverEnd:
    "radial-gradient(58% 75% at 50% 100%, rgb(19,49,112) 0%, rgba(21,18,84,0) 100%)",
  mouseLeave:
    "radial-gradient(58% 75% at 50% 100%, rgba(19,49,112,0) 0%, rgba(21,18,84,0) 100%)",
};
const BLUE_GLOW_SH = {
  default: "inset 0px 0px 0px 0px rgba(93,91,245,0)",
  hoverStart: "inset 0px -12px 12px -7px rgba(93,91,245,0.72)",
  hoverEnd: "inset 0px -12px 12px -7px rgba(93,91,245,0.72)",
  mouseLeave: "inset 0px -12px 12px -7px rgba(93,91,245,0)",
};
const INNER_BG = {
  default:
    "radial-gradient(53% 100% at 50% 77.7%, rgba(31,26,105,0) 0%, rgba(21,18,84,0) 100%)",
  hoverStart:
    "radial-gradient(53% 100% at 50% 77.7%, rgb(31,26,105) 0%, rgba(21,18,84,0) 100%)",
  hoverEnd:
    "radial-gradient(53% 100% at 50% 77.7%, rgb(31,26,105) 0%, rgba(21,18,84,0) 100%)",
  mouseLeave:
    "radial-gradient(53% 100% at 50% 77.7%, rgba(31,26,105,0) 0%, rgba(21,18,84,0) 100%)",
};
const INNER_SH = {
  default: "inset 0px 0px 0px 0px rgba(36,31,184,0)",
  hoverStart: "inset 0px -27px 27px -10px rgba(36,31,184,0.46)",
  hoverEnd: "inset 0px -27px 27px -10px rgba(36,31,184,0.46)",
  mouseLeave: "inset 0px -27px 27px -10px rgba(36,31,184,0)",
};
const INNER_OP = { default: 1, hoverStart: 1, hoverEnd: 1, mouseLeave: 1 };
const PULSE_BG = {
  default:
    "radial-gradient(129% 219% at 50% 88.7%, rgba(38,118,209,0.5) 0%, rgba(32,69,138,0) 0.139%, rgba(32,69,138,0) 2.717%)",
  hoverStart:
    "radial-gradient(129% 219% at 50% 88.7%, rgba(32,69,138,0) 0.139%, rgba(38,118,209,0.5) 0.285%, rgba(32,69,138,0) 50.695%)",
  hoverEnd:
    "radial-gradient(129% 219% at 50% 88.7%, rgba(32,69,138,0) 0%, rgba(38,118,209,0.5) 99.217%, rgba(32,69,138,0) 100%)",
  mouseLeave:
    "radial-gradient(129% 219% at 50% 88.7%, rgba(32,69,138,0) 0%, rgba(38,118,209,0.5) 99.217%, rgba(32,69,138,0) 100%)",
};
const PULSE_OP = {
  default: 0.6,
  hoverStart: 0.6,
  hoverEnd: 0.6,
  mouseLeave: 0,
};

/* --- Input capability helpers --- */
const canHover =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;
const TOUCH_CLICK_GUARD_MS = 700;

/* --- Galaxy Button --- */
function GalaxyButton({ label = "DOWNLOAD", onClick, active, disabled }) {
  const [phase, setPhase] = useState("default");
  const timerRef = useRef(null);
  const lastTouchTsRef = useRef(0);
  const isHovered = phase === "hoverStart" || phase === "hoverEnd";

  const onEnter = useCallback(() => {
    if (!canHover) return;
    clearTimeout(timerRef.current);
    setPhase("hoverStart");
    timerRef.current = setTimeout(() => setPhase("hoverEnd"), 400);
  }, []);
  const onLeave = useCallback(() => {
    if (!canHover) return;
    clearTimeout(timerRef.current);
    setPhase("mouseLeave");
    timerRef.current = setTimeout(() => setPhase("default"), 400);
  }, []);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handlePress = useCallback(
    (event) => {
      if (disabled) return;
      event?.stopPropagation?.();
      onClick && onClick();
    },
    [disabled, onClick],
  );

  const particleOn = isHovered || active ? 1 : 0;
  const blurParticleOn = isHovered || active ? 0.4 : 0;
  /* When hover is unavailable, use the active state to keep the glow responsive. */
  const glowPhase = canHover ? phase : active ? "hoverEnd" : "default";
  const W = 260,
    H = 56;
  const tMask =
    glowPhase === "hoverStart" || glowPhase === "hoverEnd" ? tSp2 : tSp1;

  return (
    <motion.div
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onTouchEnd={(e) => {
        lastTouchTsRef.current = Date.now();
        e.preventDefault();
        handlePress(e);
      }}
      onClick={(e) => {
        if (Date.now() - lastTouchTsRef.current < TOUCH_CLICK_GUARD_MS) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        handlePress(e);
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: W,
        height: H,
        borderRadius: 9999,
        position: "relative",
        cursor: disabled ? "default" : "pointer",
        overflow: "visible",
        pointerEvents: "auto",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
        transform: active ? "scale(0.94)" : "scale(1)",
        transition: "transform 220ms cubic-bezier(0.175,0.885,0.320,1.275)",
        flexShrink: 0,
        zIndex: 10,
      }}
    >
      {/* Border Fixed */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 9999,
          overflow: "hidden",
          background:
            "linear-gradient(180deg, rgb(126,133,181) 0%, rgb(126,133,181) 100%)",
        }}
      />

      {/* Border + Glow mask */}
      <motion.div
        animate={{
          WebkitMask: BORDER_MASK[glowPhase],
          mask: BORDER_MASK[glowPhase],
        }}
        transition={tMask}
        style={{
          position: "absolute",
          top: -110,
          bottom: -110,
          left: -84,
          right: -84,
          pointerEvents: "none",
          overflow: "visible",
        }}
      >
        <motion.div
          animate={{ boxShadow: GLOW_SHADOW[glowPhase] }}
          transition={tSp1}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: W,
            height: H,
            borderRadius: 9999,
            overflow: "hidden",
          }}
        />
        <motion.div
          animate={{ background: BORDER_BG[glowPhase] }}
          transition={tSp1}
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
            width: W,
            height: H,
            borderRadius: 9999,
            overflow: "hidden",
          }}
        />
      </motion.div>

      {/* Dark BG */}
      <div
        style={{
          position: "absolute",
          inset: 2,
          borderRadius: 9999,
          backgroundColor: "rgb(10,6,9)",
          overflow: "hidden",
        }}
      >
        {/* Particles */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 9999,
            overflow: "hidden",
            zIndex: 4,
            WebkitMask:
              "radial-gradient(50% 142% at 50% 100%, rgb(0,0,0) 0%, rgba(0,0,0,0) 100%) add",
            mask: "radial-gradient(50% 142% at 50% 100%, rgb(0,0,0) 0%, rgba(0,0,0,0) 100%) add",
          }}
        >
          <motion.div
            animate={{ opacity: blurParticleOn }}
            transition={tSp1}
            style={{ position: "absolute", inset: 0, top: -24 }}
          >
            <ParticleCanvas
              colors={[
                "rgb(96,143,242)",
                "rgb(171,182,237)",
                "rgb(205,247,255)",
              ]}
              count={20}
              speed={0.25}
              sizeMin={1.5}
              sizeMax={4}
              opacity={0.8}
              blur={1}
              active={isHovered || active}
            />
          </motion.div>
          <motion.div
            animate={{ opacity: particleOn }}
            transition={tSp1}
            style={{ position: "absolute", inset: 0, top: -24 }}
          >
            <ParticleCanvas
              colors={[
                "rgb(96,143,242)",
                "rgb(171,182,237)",
                "rgb(205,247,255)",
              ]}
              count={70}
              speed={0.4}
              sizeMin={0.8}
              sizeMax={2.5}
              opacity={0.9}
              active={isHovered || active}
            />
          </motion.div>
          <motion.div
            animate={{ opacity: particleOn }}
            transition={tSp1}
            style={{ position: "absolute", inset: 0, top: -24 }}
          >
            <ParticleCanvas
              colors={[
                "rgb(131,129,214)",
                "rgb(98,87,217)",
                "rgb(131,145,247)",
              ]}
              count={130}
              speed={0.8}
              sizeMin={0.4}
              sizeMax={1.5}
              opacity={1}
              active={isHovered || active}
            />
          </motion.div>
        </div>
        {/* Blue Glow */}
        <motion.div
          animate={{
            background: BLUE_GLOW_BG[glowPhase],
            boxShadow: BLUE_GLOW_SH[glowPhase],
          }}
          transition={tSp1}
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 9999,
            overflow: "hidden",
            zIndex: 3,
          }}
        />
        {/* Pulse Animated */}
        <motion.div
          animate={{
            background: PULSE_BG[glowPhase],
            opacity: PULSE_OP[glowPhase],
          }}
          transition={
            glowPhase === "hoverEnd"
              ? tSp3
              : glowPhase === "mouseLeave"
                ? tSp2
                : tInstant
          }
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 9999,
            overflow: "hidden",
            filter: "blur(5px)",
            zIndex: 2,
          }}
        />
        {/* Inner Glow */}
        <motion.div
          animate={{
            background: INNER_BG[glowPhase],
            boxShadow: INNER_SH[glowPhase],
            opacity: INNER_OP[glowPhase],
          }}
          transition={tSp1}
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: 9999,
            overflow: "hidden",
            zIndex: 1,
          }}
        />
      </div>

      {/* Edge ring */}
      <div
        style={{
          position: "absolute",
          inset: 2,
          borderRadius: 9999,
          pointerEvents: "none",
          zIndex: 7,
          boxShadow:
            "inset 0px 0px 2px 3px rgba(0,0,0,0.3), inset 0px 0px 4px 8px rgba(89,98,201,0.25)",
        }}
      />

      {/* Label */}
      <div
        style={{
          position: "relative",
          zIndex: 9,
          fontFamily: '"Barlow Condensed", "Arial Narrow", sans-serif',
          fontWeight: 700,
          fontSize: 15,
          letterSpacing: "2.6px",
          textTransform: "uppercase",
          userSelect: "none",
          whiteSpace: "nowrap",
          textRendering: "geometricPrecision",
          textAlign: "center",
          padding: "0 18px",
          lineHeight: 1,
          filter: "drop-shadow(0 0 10px rgba(160, 210, 255, 0.18))",
          height: 15,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <AnimatePresence>
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 8, scale: 0.95, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, scale: 1, filter: "blur(0px)" }}
            exit={{ opacity: 0, y: -8, scale: 1.05, filter: "blur(4px)" }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            style={{
              position: "absolute",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundImage:
                "linear-gradient(180deg, rgb(245,247,251) 0%, rgb(205,212,224) 58%, rgb(138,148,166) 100%)",
            }}
          >
            {label}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

/* --- Sub button --- */
function SubBtn({ svgIcon, label, onClick, isOpen, side, disabled }) {
  const offsetX =
    side === "left"
      ? "calc(-50% - 86px)"
      : side === "right"
        ? "calc(-50% + 86px)"
        : "-50%";
  const openDelay =
    side === "left" ? "60ms" : side === "center" ? "30ms" : "0ms";
  const lastTouchTsRef = useRef(0);
  const handleAction = disabled
    ? undefined
    : (e) => {
        e.stopPropagation();
        onClick && onClick();
      };
  return (
    <button
      onTouchEnd={
        handleAction
          ? (e) => {
              lastTouchTsRef.current = Date.now();
              e.preventDefault();
              handleAction(e);
            }
          : undefined
      }
      onClick={
        handleAction
          ? (e) => {
              if (
                Date.now() - lastTouchTsRef.current < TOUCH_CLICK_GUARD_MS
              ) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              handleAction(e);
            }
          : undefined
      }
      style={{
        position: "absolute",
        width: 60,
        height: 60,
        borderRadius: "50%",
        border:
          "1px solid " +
          (disabled
            ? "rgba(60,65,80,0.5)"
            : isOpen
              ? "rgba(165,177,198,0.42)"
              : "rgba(123,137,164,0.26)"),
        cursor: disabled ? "default" : "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 3,
        bottom: 6,
        left: "50%",
        zIndex: 5,
        transform: isOpen
          ? "translate(" + offsetX + ", -56px) scale(1)"
          : "translate(-50%, 0px) scale(0.5)",
        opacity: isOpen ? 1 : 0,
        background: disabled
          ? "linear-gradient(180deg, rgba(22,24,30,1) 0%, rgba(16,18,24,1) 100%)"
          : "radial-gradient(circle at 50% 32%, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.03) 16%, rgba(22,27,38,0.98) 62%), linear-gradient(180deg, rgba(20,24,34,0.98) 0%, rgba(12,15,23,0.98) 100%)",
        boxShadow: disabled
          ? "inset 0 1px 0 rgba(255,255,255,0.04), inset 0 0 0 1px rgba(60,65,80,0.12)"
          : "inset 0 1px 0 rgba(255,255,255,0.08), inset 0 0 0 1px rgba(120,132,154,0.06), 0 12px 22px rgba(4,8,18,0.34)",
        transition:
          "transform 320ms cubic-bezier(0.935,0,0.340,1.330), opacity 200ms ease",
        transitionDelay: isOpen ? openDelay : "0ms",
        willChange: "transform, opacity",
        pointerEvents: disabled || !isOpen ? "none" : "auto",
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <div style={{ opacity: disabled ? 0.25 : 1 }}>{svgIcon}</div>
      <span
        style={{
          fontFamily: '"Barlow Condensed", "Arial Narrow", sans-serif',
          fontWeight: 700,
          fontSize: 9,
          letterSpacing: "1.1px",
          textTransform: "uppercase",
          background: disabled
            ? "linear-gradient(180deg, #4a4f5e 0%, #363b48 100%)"
            : "linear-gradient(180deg, #e2e7ef 0%, #b1bccf 100%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
        }}
      >
        {label}
      </span>
    </button>
  );
}

/* --- Exportable Floating Download Bar --- */
export default function FloatingDownloadBar({
  onDownloadTorrent,
  onDownloadArchive,
  onMiddleAction,
  hasTorrent = true,
  hasArchive = true,
}) {
  const { isTester, isBanned, hasAccess } = useUser();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle");
  const resetStatusTimeoutRef = useRef(null);
  const showMiddleButton = isTester();
  const torrentDisabled = !hasTorrent || isBanned();

  const handleDownload = async (type) => {
    if (status !== "idle") return;

    const action = type === "torrent" ? onDownloadTorrent : onDownloadArchive;
    if (!action) return;

    clearTimeout(resetStatusTimeoutRef.current);
    setStatus("sending");
    setOpen(false);

    try {
      const result = await action();

      if (result === false) {
        setStatus("idle");
        return;
      }

      setStatus("success");
      resetStatusTimeoutRef.current = setTimeout(() => {
        setStatus("idle");
      }, 3000);
    } catch (error) {
      setStatus("idle");
    }
  };

  const handleMainClick = () => {
    if (status !== "idle") return;

    // Без доступа — сразу модалка оплаты, без доп. кнопок
    if (!hasAccess()) {
      onDownloadTorrent?.();
      return;
    }

    setOpen((v) => !v);
  };

  useEffect(() => {
    const close = () => {
      if (open) setOpen(false);
    };
    if (open) {
      window.addEventListener("click", close);
      return () => window.removeEventListener("click", close);
    }
  }, [open]);

  useEffect(() => {
    return () => clearTimeout(resetStatusTimeoutRef.current);
  }, []);

  const mainButtonLabel =
    status === "sending"
      ? "SENDING"
      : status === "success"
        ? "SENT TO TELEGRAM!"
        : "DOWNLOAD";
  const mainButtonActive = open || status !== "idle";
  const mainButtonDisabled = status !== "idle";

  return (
    <div
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        padding: "0 24px 24px",
        willChange: "transform",
        contain: "layout style paint",
        background:
          "linear-gradient(to bottom, rgba(20,20,24,0) 0%, rgba(20,20,24,0) 55%, rgba(20,20,24,0.85) 80%, rgba(20,20,24,1) 100%)",
        pointerEvents: "none",
      }}
    >
      <GooFilter />

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        {/* Goo wrapper */}
        <div
          style={{
            filter: "url('#goo-effect')",
            position: "relative",
            width: 280,
            height: 200,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            isolation: "isolate",
            overflow: "visible",
            pointerEvents: "none",
          }}
        >
          <SubBtn
            svgIcon={<TorrentIcon />}
            label="Torrent"
            isOpen={open}
            side="left"
            disabled={torrentDisabled}
            onClick={() => handleDownload("torrent")}
          />
          {showMiddleButton && (
            <SubBtn
              svgIcon={<ArchiveArrowBackIcon />}
              label="Convert"
              isOpen={open}
              side="center"
              onClick={() => {
                setOpen(false);
                onMiddleAction?.();
              }}
            />
          )}
          <SubBtn
            svgIcon={<ArchiveIcon />}
            label="Archive"
            isOpen={open}
            side="right"
            disabled={!hasArchive}
            onClick={() => handleDownload("archive")}
          />
          <GalaxyButton
            label={mainButtonLabel}
            active={mainButtonActive}
            disabled={mainButtonDisabled}
            onClick={handleMainClick}
          />
        </div>
      </div>
    </div>
  );
}
